import { useCallback, useEffect, useState } from 'react';

import {
  ArrowRightOutlined,
  DeleteOutlined,
  ExclamationCircleOutlined,
  RiseOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  Col,
  Empty,
  Modal,
  PageHeader,
  Row,
  Skeleton,
  Table,
  Tabs,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useHistory, useLocation } from 'react-router';

import * as S from './styled';

import { getMeterDeviceLists } from '../../../../hooks/smartcharging';
import {
  deleteVirtualGroupChargebox,
  getVirtualGroup,
  getVirtualGroupChargerboxes,
  getVirtualGroupDynamicPricing,
  getVirtualGroupEvents,
  getVirtualGroupSmartCharging,
  getVirtualGroupTransactions,
} from '../../../../hooks/virtualgroup';
import { formatFullDate } from '../../../../utils/dates';
import { COLORS } from '../../../../utils/theme/colors';
import { MeterDevice } from '../../../molecules/MeterDevice/MeterDevice';
import { Pagination } from '../../../molecules/Pagination/Pagination';
import SmartChargingCreatePage from '../../SmartChargingCreatePage/SmartChargingCreatePage';
import SmartChargingEditPage from '../../SmartChargingEditPage/SmartChargingEditPage';
import VirtualGroupDynamicPricingPage from '../../VirtualGroupDynamicPricingPage/VirtualGroupDynamicPricingPage';

interface VirtualGroupEventsData {
  event_id: string;
  virtual_group_id: string;
  timestamp: string;
  description: string;
}

interface VirtualGroupTransactionsData extends VirtualGroupEventsData {}

interface VirtualGroupEvents {
  error: boolean;
  data: VirtualGroupEventsData[] | null;
  loading: boolean;
  request: Function;
  pagination: {
    page: number;
    pageSize: number;
    total?: number;
  };
}

interface VirtualGroupTransactions extends Omit<VirtualGroupEvents, 'data'> {
  data: VirtualGroupTransactionsData[] | null;
}

const VIRTUAL_GROUP_DEFAULT_PAGE = 1;
const VIRTUAL_GROUP_DEFAULT_PAGE_SIZE = 4;

const VirtualGroupPage = () => {
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const { pathname, state }: any = useLocation();

  const [virtualGroup, setVirtualGroup] = useState<any>();
  const [loadingDeleteChargebox, setLoadingDeleteChargebox] = useState(false);

  const [activeTab, setActiveTab] = useState(
    state?.activeTab || 'virtual-group-tab-1',
  );

  const [loadingVirtualGroupEvents, setLoadingVirtualGroupEvents] =
    useState<boolean>(false);
  const [loadingVirtualGroupTransactions, setLoadingVirtualGroupTransactions] =
    useState<boolean>(false);
  const [loadingChargeboxs, setLoadingChargeboxs] = useState(false);

  const [id] = useState(pathname.split('/').pop());

  const authUser: any = JSON.parse(localStorage.getItem('auth-user') as string);

  const [hasAddChargeboxPermission] =
    authUser?.permissions?.filter(
      (permission) =>
        permission.role_id === 'DASH_VIRTUAL_GROUP_EDIT_CHARGEBOXES',
    ) || [];

  const shouldShowSmartCharging = authUser?.permissions.some(
    (permission) =>
      permission.role_id === 'DASH_SMART_CHARGING_CREATE' &&
      !!permission.status,
  );

  const columns: ColumnsType<any> = [
    {
      title: 'Carregador',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'ID',
      dataIndex: 'charge_box_code',
      key: 'charge_box_code',
    },
    {
      title: 'Configurações avançadas',
      dataIndex: 'has_config_tec',
      key: 'has_config_tec',
      align: 'center',
      render: (value) => <S.Bullet active={value} />,
    },
    {
      title: 'Excluir',
      dataIndex: 'remove',
      key: 'remove',
      align: 'center',
      render: (_, value) => (
        <S.Button
          danger
          ghost
          icon={<DeleteOutlined />}
          shape='circle'
          onClick={() => confirm(value)}
        />
      ),
    },
  ];

  const modalDeleteChargeboxSuccess = () => {
    Modal.success({
      title: 'O carregador foi excluído com sucesso!',
    });
  };

  const modalDeleteChargeboxError = () => {
    Modal.error({
      title: 'Ocorreu um erro ao excluir o carregador selecionado!',
    });
  };

  const responseMeterDevice = useCallback(() => {
    if (!virtualGroup?.meter_device || !shouldShowSmartCharging) {
      return null;
    }

    return (
      <MeterDevice
        meterDevice={virtualGroup.meter_device}
        style={{
          padding: 10,
          position: 'absolute',
          top: 8,
          right: 30,
        }}
      />
    );
  }, [virtualGroup?.meter_device]);

  const confirm = (value: any) => {
    Modal.confirm({
      title: `Deseja excluir o carregador ${value?.description}?`,
      icon: <ExclamationCircleOutlined />,
      okText: 'Excluir',
      cancelText: 'Cancelar',
      okButtonProps: {
        danger: true,
        loading: loadingDeleteChargebox,
      },
      onOk: async () => {
        setLoadingDeleteChargebox(true);
        try {
          await deleteVirtualGroupChargebox(value?.virtual_group_charge_box_id);

          setLoadingChargeboxs(true);

          try {
            const { data } = await getVirtualGroupChargerboxes(id);

            setChargeboxs((value) => ({
              ...value,
              data: data.content?.map((value, key) => ({
                ...value,
                key: `chargebox-${key}`,
              })),
            }));
          } finally {
            setTimeout(() => setLoadingChargeboxs(false), 2000);
          }

          modalDeleteChargeboxSuccess();
        } catch {
          modalDeleteChargeboxError();
        } finally {
          setLoadingDeleteChargebox(false);
        }
      },
    });
  };

  const handleVirtualGroupDynamicPricing = useCallback(async (id) => {
    setLoading(true);

    try {
      const { data: virtualGroupDynamicPricing } =
        await getVirtualGroupDynamicPricing(id);

      setVirtualGroup((prev) => ({
        ...prev,
        dynamic_pricing: {
          enabled: virtualGroupDynamicPricing.enabled,
          connector1_dynamic_pricing_id:
            virtualGroupDynamicPricing.connector1_dynamic_pricing_id,
          connector2_dynamic_pricing_id:
            virtualGroupDynamicPricing.connector2_dynamic_pricing_id,
          connector3_dynamic_pricing_id:
            virtualGroupDynamicPricing.connector3_dynamic_pricing_id,
        },
      }));
    } finally {
      setTimeout(() => setLoading(false), 2000);
    }
  }, []);

  const handleVirtualGroupSmartCharging = useCallback(
    async (id) => {
      try {
        const { data: virtualGroupSmartCharging } =
          await getVirtualGroupSmartCharging(id);

        const meterDevice = await getMeterDeviceLists(
          virtualGroupSmartCharging?.meter_device_remote_id,
        );

        setVirtualGroup((prev) => ({
          ...prev,
          meter_device: meterDevice?.data?.length && {
            ...meterDevice?.data[0],
            status: meterDevice?.data[0]?.meter?.communication_status,
            phaseMaxValue: meterDevice?.data[0]?.current?.phase
              ? Math.ceil(
                  Math.max(
                    ...Object.values(
                      meterDevice?.data[0]?.current?.phase as {
                        [key: string]: number;
                      },
                    ),
                  ),
                ).toFixed(1)
              : 0,
          },
          smart_charging: virtualGroupSmartCharging,
        }));
      } finally {
        await handleVirtualGroupDynamicPricing(id);
      }
    },
    [handleVirtualGroupDynamicPricing],
  );

  useEffect(() => {
    (async () => {
      setLoading(true);

      try {
        const { data } = await getVirtualGroup(id);

        setVirtualGroup(data);
      } finally {
        await handleVirtualGroupSmartCharging(id);
      }
    })();
  }, [id, handleVirtualGroupSmartCharging]);

  const tryToGetVirtualGroupChargeboxs = useCallback(
    async (page: number = 1, pageSize: number = 10) => {
      setLoadingChargeboxs(true);

      try {
        const { data } = await getVirtualGroupChargerboxes(id, page, pageSize);

        setChargeboxs((value) => ({
          ...value,
          data: data.content?.map((value, key) => ({
            ...value,
            key: `chargebox-${key}`,
          })),
          pagination: {
            page,
            pageSize,
            total: data.totalElements,
          },
        }));
      } finally {
        setTimeout(() => setLoadingChargeboxs(false), 2000);
      }
    },
    [id],
  );

  const [chargeboxs, setChargeboxs] = useState<any>({
    error: false,
    data: null,
    loading: false,
    request: tryToGetVirtualGroupChargeboxs,
    pagination: { page: 1, pageSize: 10 },
  });

  const tryToGetVirtualGroupEvents = useCallback(
    async (page: number = 1, pageSize: number = 10) => {
      setLoadingVirtualGroupEvents(true);

      try {
        const { data } = await getVirtualGroupEvents(id, page, pageSize);

        setVirtualGroupEvents((value) => ({
          ...value,
          data: data.content.map((value, key) => ({
            ...value,
            key: `events-${key}`,
          })),
          pagination: {
            page,
            pageSize,
            total: data.totalElements,
          },
        }));
      } finally {
        setTimeout(() => setLoadingVirtualGroupEvents(false), 1000);
      }
    },
    [id],
  );

  const [virtualGroupEvents, setVirtualGroupEvents] =
    useState<VirtualGroupEvents>({
      error: false,
      data: null,
      loading: false,
      request: tryToGetVirtualGroupEvents,
      pagination: { page: 1, pageSize: 4 },
    });

  const tryToGetVirtualGroupTransactions = useCallback(
    async (page: number = 1, pageSize: number = 10) => {
      setLoadingVirtualGroupTransactions(true);

      try {
        const { data } = await getVirtualGroupTransactions(id, page, pageSize);

        setVirtualGroupTransactions((value) => ({
          ...value,
          data: data.content.map((value, key) => ({
            ...value,
            key: `transactions-${key}`,
          })),
          pagination: {
            page,
            pageSize,
            total: data.totalElements,
          },
        }));
      } finally {
        setTimeout(() => setLoadingVirtualGroupTransactions(false), 1000);
      }
    },
    [id],
  );

  const [virtualGroupTransactions, setVirtualGroupTransactions] =
    useState<VirtualGroupTransactions>({
      error: false,
      data: null,
      loading: false,
      request: tryToGetVirtualGroupTransactions,
      pagination: {
        page: VIRTUAL_GROUP_DEFAULT_PAGE,
        pageSize: VIRTUAL_GROUP_DEFAULT_PAGE_SIZE,
      },
    });

  useEffect(() => {
    (async () => {
      await tryToGetVirtualGroupEvents(
        VIRTUAL_GROUP_DEFAULT_PAGE,
        activeTab === 'virtual-group-tab-4'
          ? 10
          : VIRTUAL_GROUP_DEFAULT_PAGE_SIZE,
      );
    })();
  }, [activeTab, tryToGetVirtualGroupEvents]);

  useEffect(() => {
    (async () => {
      tryToGetVirtualGroupChargeboxs();
    })();
  }, [tryToGetVirtualGroupChargeboxs]);

  useEffect(() => {
    (async () => {
      await tryToGetVirtualGroupTransactions(
        VIRTUAL_GROUP_DEFAULT_PAGE,
        VIRTUAL_GROUP_DEFAULT_PAGE_SIZE,
      );
    })();
  }, [tryToGetVirtualGroupTransactions]);

  const eventsColumns: ColumnsType<any> = [
    {
      dataIndex: 'icon',
      key: 'icon',
      width: '5%',

      render: () => (
        <S.TableIcon>
          <RiseOutlined />
        </S.TableIcon>
      ),
    },
    {
      dataIndex: 'description',
      key: 'description',
      width: '75%',
    },
    {
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (value) => <td className='timestamp'>{formatFullDate(value)}</td>,
    },
  ];

  return (
    <>
      <S.Wrapper>
        <PageHeader
          style={{ width: '100%' }}
          onBack={() => history.push('/virtual-groups')}
          title={
            <Row justify='space-between' align='middle'>
              <div>
                <SettingOutlined
                  style={{ position: 'relative', marginRight: 16 }}
                  color={COLORS.black}
                  height={20}
                  width={20}
                />
              </div>

              <div>
                <Skeleton
                  loading={loading}
                  paragraph={false}
                  title={{
                    width: 100,
                  }}
                >
                  {virtualGroup?.name}
                </Skeleton>
              </div>
            </Row>
          }
          extra={[
            <S.Button
              key='virtual-group-edit-button'
              size='large'
              onClick={() => history.push(`/virtual-groups/edit/${id}`)}
            >
              Editar Configuração
            </S.Button>,
          ]}
        />

        <S.Blank>
          <Tabs
            defaultActiveKey='1'
            onChange={(key) => setActiveTab(key)}
            activeKey={activeTab}
          >
            <Tabs.TabPane tab='Overview' key='virtual-group-tab-1'>
              <Row gutter={[16, 16]}>
                <Col md={12} xs={24} sm={24} lg={8}>
                  <S.OverviewBox>
                    <Typography.Title level={5}>
                      <S.Title>Detalhes</S.Title>
                    </Typography.Title>
                    <Typography.Text>
                      <S.Text>{virtualGroup?.description}</S.Text>
                    </Typography.Text>
                    <S.CardWrapper>
                      <S.Card>
                        <S.Icon />
                        <div>
                          <S.Legend>Quantidade</S.Legend>
                          <Skeleton
                            loading={loading}
                            paragraph={false}
                            title={{
                              width: 100,
                              style: { marginTop: 8 },
                            }}
                          >
                            {chargeboxs?.data?.length || 0} carregador(es)
                          </Skeleton>
                        </div>
                      </S.Card>
                      <S.Card>
                        <S.Icon />
                        <div>
                          <S.Legend>Perfil de Precificação</S.Legend>
                          <Skeleton
                            loading={loading}
                            paragraph={false}
                            title={{
                              width: 100,
                              style: { marginTop: 8 },
                            }}
                          >
                            {virtualGroup?.dynamic_pricing?.enabled
                              ? 'Habilitado'
                              : 'Desabilitado'}
                          </Skeleton>
                        </div>
                      </S.Card>

                      {shouldShowSmartCharging && (
                        <S.Card>
                          <S.Icon />
                          <div>
                            <S.Legend>Smart Charging</S.Legend>
                            <Skeleton
                              loading={loading}
                              paragraph={false}
                              title={{
                                width: 100,
                                style: { marginTop: 8 },
                              }}
                            >
                              {virtualGroup?.smart_charging?.enabled
                                ? 'Habilitado'
                                : 'Desabilitado'}
                            </Skeleton>
                          </div>
                        </S.Card>
                      )}
                    </S.CardWrapper>
                  </S.OverviewBox>
                </Col>

                {shouldShowSmartCharging && (
                  <Col md={12} xs={24} sm={24} lg={8}>
                    <Skeleton
                      loading={loadingVirtualGroupEvents}
                      active
                      paragraph={{
                        rows: 10,
                        width: 10,
                      }}
                    >
                      <S.OverviewBox>
                        <Typography.Title
                          level={5}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <S.Title>Eventos</S.Title>
                          <S.SeeAllContainer
                            onClick={() => setActiveTab('virtual-group-tab-4')}
                          >
                            {virtualGroupEvents?.data?.length !== 0 &&
                              'Ver todos'}
                            <ArrowRightOutlined style={{ color: '#9499a2' }} />
                          </S.SeeAllContainer>
                        </Typography.Title>
                        <S.CardEmptyWrapper>
                          {virtualGroupEvents?.data?.length ? (
                            virtualGroupEvents?.data?.map((value, key) => (
                              <S.CardEmpty key={key}>
                                <S.Icon />
                                <div>
                                  <S.LegendDate>
                                    {formatFullDate(value.timestamp)}
                                  </S.LegendDate>
                                  <S.CardText>{value.description}</S.CardText>
                                </div>
                              </S.CardEmpty>
                            ))
                          ) : (
                            <S.CardEmptyWrapper>
                              <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description='Nenhum evento encontrado'
                                style={{ width: '100%', margin: 'auto' }}
                              />
                            </S.CardEmptyWrapper>
                          )}
                        </S.CardEmptyWrapper>
                      </S.OverviewBox>
                    </Skeleton>
                  </Col>
                )}

                <Col md={12} xs={24} sm={24} lg={8}>
                  <Skeleton
                    loading={loadingVirtualGroupTransactions}
                    active
                    paragraph={{
                      rows: 10,
                      width: 10,
                    }}
                  >
                    <S.OverviewBox>
                      <Typography.Title
                        level={5}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <S.Title>Transações</S.Title>
                        <S.SeeAllContainer
                          onClick={() => history.push('/transactions')}
                        >
                          {virtualGroupTransactions?.data?.length !== 0 &&
                            'Ver todos'}
                          <ArrowRightOutlined style={{ color: '#9499a2' }} />
                        </S.SeeAllContainer>
                      </Typography.Title>
                      <S.CardEmptyWrapper>
                        {virtualGroupTransactions?.data?.length ? (
                          virtualGroupTransactions?.data?.map((value, key) => (
                            <S.CardEmpty key={key}>
                              <S.Icon />
                              <div>
                                <S.LegendDate>
                                  {formatFullDate(value.timestamp)}
                                </S.LegendDate>
                                <S.CardText>{value.description}</S.CardText>
                              </div>
                            </S.CardEmpty>
                          ))
                        ) : (
                          <S.CardEmptyWrapper>
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description='Nenhuma transação encontrada'
                              style={{ width: '100%', margin: 'auto' }}
                            />
                          </S.CardEmptyWrapper>
                        )}
                      </S.CardEmptyWrapper>
                    </S.OverviewBox>
                  </Skeleton>
                </Col>
              </Row>
            </Tabs.TabPane>
            <Tabs.TabPane
              disabled={loading}
              tab='Carregadores'
              key='virtual-group-tab-2'
            >
              <S.Row style={{ marginBottom: 24 }}>
                <S.Col>
                  <Skeleton
                    loading={loading}
                    paragraph={false}
                    title={{
                      width: 100,
                    }}
                  >
                    <S.Legend>
                      {chargeboxs?.data?.length || 0} carregador(es)
                    </S.Legend>
                  </Skeleton>
                </S.Col>
                <S.Col>
                  {hasAddChargeboxPermission && (
                    <S.Button
                      type='primary'
                      onClick={() => {
                        history.push({
                          pathname: `/virtual-groups/edit/${id}/chargeboxes`,
                          state: { payload: virtualGroup },
                        });
                      }}
                    >
                      Adicionar carregadores
                    </S.Button>
                  )}
                </S.Col>
              </S.Row>

              <Skeleton
                loading={loadingChargeboxs}
                active
                paragraph={{
                  rows: 5,
                  width: '100%',
                }}
                title={false}
              >
                <S.TableWrapper>
                  <Table
                    dataSource={chargeboxs?.data}
                    columns={columns}
                    pagination={false}
                    locale={{ emptyText: 'Nenhum carregador encontrado' }}
                  />
                  <Pagination
                    currentPage={chargeboxs?.pagination?.page}
                    pageSize={chargeboxs?.pagination?.pageSize}
                    totalCount={chargeboxs?.pagination?.total}
                    onPageChange={(
                      page: number,
                      pageSize: number | undefined,
                    ) => {
                      chargeboxs.request(page, pageSize);
                    }}
                  />
                </S.TableWrapper>
              </Skeleton>
            </Tabs.TabPane>
            <Tabs.TabPane
              disabled={loading}
              tab='Perfil de Precificação'
              key='dynamic-pricing-tab-3'
            >
              <VirtualGroupDynamicPricingPage
                editValues={virtualGroup?.dynamic_pricing}
              />
            </Tabs.TabPane>
            {shouldShowSmartCharging && (
              <Tabs.TabPane
                disabled={loading}
                tab='Smart Charging'
                key='virtual-group-tab-4'
              >
                {virtualGroup?.smart_charging?.smart_charging_id ? (
                  <SmartChargingEditPage
                    smartChargingId={
                      virtualGroup?.smart_charging?.smart_charging_id
                    }
                  />
                ) : (
                  <SmartChargingCreatePage />
                )}
              </Tabs.TabPane>
            )}
            {shouldShowSmartCharging && (
              <Tabs.TabPane
                disabled={loading}
                tab='Eventos'
                key='virtual-group-tab-5'
              >
                <S.Div>
                  <Skeleton
                    loading={loadingVirtualGroupEvents}
                    active
                    paragraph={{
                      rows: 5,
                      width: '100%',
                    }}
                    title={false}
                  >
                    {virtualGroupEvents?.data?.length ? (
                      <S.TableEventsWrapper>
                        <Table
                          showHeader={false}
                          dataSource={virtualGroupEvents?.data}
                          columns={eventsColumns}
                          pagination={false}
                        />
                        <Pagination
                          currentPage={virtualGroupEvents?.pagination?.page}
                          pageSize={virtualGroupEvents?.pagination?.pageSize}
                          totalCount={virtualGroupEvents?.pagination?.total}
                          onPageChange={(
                            page: number,
                            pageSize: number | undefined,
                          ) => {
                            virtualGroupEvents.request(page, pageSize);
                          }}
                        />
                      </S.TableEventsWrapper>
                    ) : (
                      <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description='Nenhum evento encontrado'
                        style={{ margin: 'auto', width: '100%' }}
                      />
                    )}
                  </Skeleton>
                </S.Div>
              </Tabs.TabPane>
            )}
          </Tabs>

          {responseMeterDevice()}
        </S.Blank>
      </S.Wrapper>
    </>
  );
};

export default VirtualGroupPage;
